import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import {
    type Descendant,
    type BaseSelection,
    Editor,
    type BaseRange,
    Range,
    Transforms,
    Element as SlateElement,
} from 'slate';
import { Slate, Editable, ReactEditor } from 'slate-react';
import {
    type RenderElementProps,
    type SlateBlock,
    type TableBlock,
    renderBlockElement,
    renderLeafElement,
} from '@atlas/renderer';
import createEditorInstance from '@atlas/tool/box/slate/createEditorInstance';
import createTextBlock from '@atlas/tool/box/slate/createTextBlock';
import handleKeyDownEvent from '@atlas/tool/box/slate/handleKeyDownEvent';
import useLastRef from '@atlas/tool/box/hooks/useLastRef';
import showFloatingElement from '@atlas/tool/box/dom/showFloatingElement';
import type { TExample } from '@atlas/uicorn/preview/modules';
import type { TEditorFeature } from '@atlas/tool/box/slate/editorFeatures';
import Dialog from '@atlas/uicorn/lib/Display/Dialog';
import useRefCallback from 'use-ref-callback';
import translateFeaturesToRestrictions from '@atlas/tool/box/slate/translateFeaturesToRestrictions';
import matchNode from '@atlas/tool/box/slate/matchNode';
import fixEmptyChildren from '@atlas/tool/box/slate/fixEmptyChildren';
import createNodeResizeHandler from '@atlas/tool/box/slate/createNodeResizeHandler';
import isEmptyContent from '@atlas/tool/box/slate/isEmptyContent';
import TrashIcon from '@atlas/uicorn/lib/Icon/TrashIcon';
import IconButtonsGroup from '@atlas/uicorn/lib/Control/IconButtonsGroup';
import normalizeEditorChildren from '@atlas/tool/box/slate/normalizeEditorChildren';
import { isEqual } from 'lodash-es';
import styles from './RichInput.module.css';
import cn from 'classnames';

const RichInput = React.memo(
    React.forwardRef<TRichInputRef, TRichInputProps>(function RichInput(
        props: TRichInputProps,
        ref,
    ): React.ReactElement {
        const {
            children,
            className,
            inputClassName,
            style,
            editor,
            features,
            disabled,
            readOnly,
            value,
            defaultValue,
            buildContext,
            blockMenu,
            onChange,
            onSelect,
            onKeyDown,
            onMouseDown,
            ...editorProps
        } = props;

        const onChangeRef = useLastRef(onChange);
        const featuresRef = useLastRef<TEditorFeature[] | undefined | null>(features);

        const localEditor = useMemo<Editor>(() => {
            const currentEditor =
                editor ??
                createEditorInstance({
                    restrictions: features && translateFeaturesToRestrictions(features),
                    maxImageHeight,
                });
            const { onKeyDown } = currentEditor;
            currentEditor.onKeyDown = (event) => {
                if (handleKeyDownEvent(currentEditor, event, featuresRef.current ?? undefined)) return;
                onKeyDown?.(event);
            };
            return currentEditor;
        }, [editor]);
        useImperativeHandle(ref, () => localEditor, [localEditor]);
        const localEditorRef = useRef(localEditor);

        useEffect(() => {
            if (!value) return;
            if (value === localEditor.children) return;

            // Mute onChange before setting passed value
            const outerOnChange = onChangeRef.current;
            onChangeRef.current = undefined;

            localEditor.children = fixEmptyChildren(value);
            localEditor.onChange();

            onChangeRef.current = outerOnChange;
        }, [value]);

        const contextKeyDownHandlerRef = useRef<null | React.KeyboardEventHandler>(null);

        const onInlineMouseDown = useRefCallback<React.MouseEventHandler<HTMLDialogElement>>(() => {
            // Prevent Safari from losing input focus when clicking on the inline element
            if (/^((?!chrome|android).)*safari/i.test(window.navigator.userAgent)) {
                setTimeout(() => {
                    ReactEditor.focus(localEditorRef.current);
                });
            }
        });

        const [inline, setInline] = useState<React.ReactElement | null>(null);
        const closeInline = useRefCallback(() => {
            setInline(null);
            ReactEditor.focus(localEditorRef.current);
        });
        // TODO: Extract and optimize (maybe call ref of the child component to set the value, so the editor doesn't re-render)
        const refreshInline = useCallback(
            (change: 'value' | 'selection') => {
                contextKeyDownHandlerRef.current = null;
                if (!buildContext) return setInline(null);

                // Ensure that editor is focused
                const editorDomNode = getEditorDomElement(localEditor);
                if (!editorDomNode) return setInline(null);
                if (document.activeElement !== editorDomNode) return setInline(null);

                // Ensure that selection is inside of the editor
                const domSelection = window.getSelection();
                if (!domSelection || !domSelection.anchorNode || !domSelection.focusNode) return setInline(null);
                if (!ReactEditor.hasDOMNode(localEditor, domSelection.anchorNode)) return setInline(null);
                if (!ReactEditor.hasDOMNode(localEditor, domSelection.focusNode)) return setInline(null);

                const [inlineElement, keyDownHandler] = buildContext(localEditor, closeInline, change) ?? [];
                contextKeyDownHandlerRef.current = keyDownHandler ?? null;
                if (!inlineElement) return setInline(null);

                let unsubscribeFloating: (() => void) | void;

                const positionInline = (anchorElement: HTMLElement) => {
                    unsubscribeFloating?.();

                    if (!anchorElement) return;

                    const inlineContainerElement = anchorElement.parentElement;
                    if (!inlineContainerElement) return;

                    const container = containerRef.current;
                    if (!container) return;

                    unsubscribeFloating = showFloatingElement(inlineContainerElement, container, {
                        refreshInterval: 300,
                    });
                };

                setInline(
                    <Dialog
                        className={styles.inlineDialog}
                        onMouseUp={stopPropagation}
                        onMouseDown={onInlineMouseDown}
                        elevation="low"
                        open
                    >
                        {inlineElement}
                        <i className={styles.inlineAnchor} key={Math.random()} ref={positionInline} />
                    </Dialog>,
                );
            },
            [buildContext, closeInline, localEditor, onInlineMouseDown],
        );

        const [initialValue] = useState<TRichInputValue>(() => {
            const val = value || defaultValue || [createTextBlock()];
            const normalized = normalizeEditorChildren(val);
            if (localEditor.children.length && !isEqual(normalized, localEditor.children)) {
                console.error('RichInput: initialValue is not normalized', localEditor.children);
            }
            return normalized;
        });
        const lastValueRef = useRef(initialValue);
        const lastSelectionRef = useRef(localEditor.selection);

        const handleValueChange = useRefCallback((value: TRichInputValue) => {
            if (lastValueRef.current !== value) {
                onChangeRef.current?.(value);
                lastValueRef.current = value;
                refreshInline('value');
            }
        });

        const handleSelectionChange = useRefCallback((selection: BaseSelection) => {
            if (lastSelectionRef.current !== selection) {
                onSelect?.(selection);
                lastSelectionRef.current = selection;
                refreshInline('selection');
            }

            if (isEmptyContent(localEditor.children)) return;

            const container = ReactEditor.toDOMNode(localEditor, localEditor);
            if (container) markFocusedElements(container);
        });

        const handleKeyDown = useRefCallback<React.KeyboardEventHandler<HTMLDivElement>>((event) => {
            contextKeyDownHandlerRef.current?.(event);
            if (event.defaultPrevented) return;

            onKeyDown?.(event);
            if (event.defaultPrevented) return;

            localEditorRef.current.onKeyDown(event);
        });

        const containerRef = useRef<HTMLDivElement>(null);

        const handleClick = useRefCallback<React.MouseEventHandler<HTMLDivElement>>((event) => {
            if (
                !ReactEditor.isFocused(localEditor) &&
                (event.target === event.currentTarget || event.currentTarget.contains(event.target as Node))
            ) {
                ReactEditor.focus(localEditor);
            }
        });

        const renderBlockElement = useCallback(
            (props: RenderElementProps) => {
                if (!blockMenu) {
                    if (props.element.type === 'image') {
                        const onClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
                            const el = event.currentTarget.closest('.atlas-block-image');
                            if (!el) return;
                            const node = ReactEditor.toSlateNode(localEditor, el);
                            Transforms.removeNodes(localEditor, { at: ReactEditor.findPath(localEditor, node) });
                        };
                        return renderElement(props, {
                            editing: true,
                            extra: (
                                <IconButtonsGroup
                                    className={styles.actionButtons}
                                    data-ui-role="rich-input-action-buttons"
                                    items={[{ icon: <TrashIcon />, tooltip: 'Remove', onClick }]}
                                />
                            ),
                        });
                    }
                    return renderElement(props, { editing: true });
                }
                return React.createElement('div', { className: 'atlas-block-container' }, [
                    renderElement(props, { editing: true }),
                    blockMenu({ node: { type: 'paragraph', children: [] }, editor: localEditor }),
                ]);
            },
            [blockMenu, localEditor],
        );

        const handleImageResizeRight = useRefCallback<React.MouseEventHandler<HTMLElement>>(
            createNodeResizeHandler(localEditor, 'atlas-block-image__resize-right', {
                minSize: minImageSize,
                maxHeight: editor?.maxImageHeight ?? maxImageHeight,
                direction: 'right',
            }),
        );

        const handleImageResizeLeft = useRefCallback<React.MouseEventHandler<HTMLElement>>(
            createNodeResizeHandler(localEditor, 'atlas-block-image__resize-left', {
                minSize: minImageSize,
                maxHeight: editor?.maxImageHeight ?? maxImageHeight,
                direction: 'left',
            }),
        );

        const handleImageResizeBottom = useRefCallback<React.MouseEventHandler<HTMLElement>>(
            createNodeResizeHandler(localEditor, 'atlas-block-image__resize-bottom', {
                minSize: minImageSize,
                maxHeight: editor?.maxImageHeight ?? maxImageHeight,
                direction: 'bottom',
            }),
        );

        const handleEmbedResize = useRefCallback<React.MouseEventHandler<HTMLElement>>((event) => {
            const resizeEl = event.target;
            if (!(resizeEl instanceof HTMLElement)) return;
            if (!resizeEl.classList.contains('atlas-block-embed__resize')) return;

            event.preventDefault();
            const MIN_EMBED_HEIGHT = 100;
            const parentEl = resizeEl.parentElement as HTMLElement;
            const initialEmbedHeight = parentEl.offsetHeight;
            const initialCursorY = event.clientY;
            const handleMove = (event: MouseEvent) => {
                const change = event.clientY - initialCursorY;
                const modifiedHeight = initialEmbedHeight + change;
                const height = modifiedHeight > MIN_EMBED_HEIGHT ? modifiedHeight : MIN_EMBED_HEIGHT;
                Transforms.setNodes(
                    localEditor,
                    { height },
                    {
                        match: (node) => SlateElement.isElement(node) && node.type === 'embed',
                    },
                );
            };
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', () => window.removeEventListener('mousemove', handleMove), {
                once: true,
            });
        });

        const handleTableColumnResize = useRefCallback<React.MouseEventHandler<HTMLElement>>((event) => {
            const resizeEl = event.target;
            if (!(resizeEl instanceof HTMLElement)) return;
            if (!resizeEl.classList.contains('atlas-block-table-cell__resize')) return;

            const tableNode = matchNode<TableBlock>(
                localEditor,
                (node) => SlateElement.isElement(node) && node.type === 'table',
            );
            if (!tableNode) return;

            const parentEl = resizeEl.parentElement;
            if (!parentEl?.parentElement) return;

            const columnIndex = Array.from(parentEl.parentElement.children).indexOf(parentEl);
            if (columnIndex < 0) return;

            event.preventDefault();
            const initialCellWidth = parentEl.offsetWidth;
            const initialCursorX = event.clientX;

            const handleMove = (event: MouseEvent) => {
                const change = event.clientX - initialCursorX;
                const modifiedWidth = initialCellWidth + change;
                const width = modifiedWidth > minColumnWidth ? modifiedWidth : minColumnWidth;
                const columns = tableNode.columns ? [...tableNode.columns] : new Array(columnIndex).fill({});
                columns[columnIndex] = { ...columns[columnIndex], width };
                Transforms.setNodes(
                    localEditor,
                    { columns },
                    {
                        match: (node) => SlateElement.isElement(node) && node.type === 'table',
                    },
                );
            };
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', () => window.removeEventListener('mousemove', handleMove), {
                once: true,
            });
        });

        const handleMouseDown = useRefCallback<React.MouseEventHandler<HTMLDivElement>>((event) => {
            onMouseDown?.(event);
            if (event.defaultPrevented) return;
            handleImageResizeRight(event);
            handleImageResizeLeft(event);
            handleImageResizeBottom(event);
            handleEmbedResize(event);
            handleTableColumnResize(event);
        });

        const inputElement = (
            <div
                className={cn(styles.richInput, className)}
                data-ui-role="rich-input"
                style={style}
                onClick={handleClick}
                ref={containerRef}
                translate="no"
            >
                <Editable
                    {...editorProps}
                    className={cn(styles.richInputEditable, inputClassName)}
                    data-ui-role="rich-input-editable"
                    renderElement={renderBlockElement}
                    renderLeaf={renderLeaf}
                    onMouseDown={handleMouseDown}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    readOnly={disabled}
                />
                {inline}
            </div>
        );

        return (
            <Slate
                editor={localEditor}
                initialValue={initialValue}
                onValueChange={handleValueChange}
                onSelectionChange={handleSelectionChange}
            >
                {children ? children(inputElement) : inputElement}
            </Slate>
        );
    }),
);

export default RichInput;

// #region Helpers

const minColumnWidth = 30;
const minImageSize = 32;
export const maxImageHeight = 400;

function stopPropagation(event: React.UIEvent) {
    event.stopPropagation();
}

function getEditorDomElement(editor: Editor): HTMLElement | null {
    try {
        return ReactEditor.toDOMNode(editor, editor) || null;
    } catch (e) {
        return null;
    }
}

const renderElement = renderBlockElement.bind(null, React.createElement, React.Fragment);

const renderLeaf = renderLeafElement.bind(null, React.createElement, React.Fragment);

const focusClassName = 'atlas-focus';

function markFocusedElements(container: HTMLElement) {
    const selection = window.getSelection();
    if (!selection?.focusNode) return;
    if (!container.contains(selection.focusNode)) return;

    container.querySelectorAll(`.${focusClassName}`).forEach((node) => node.classList.remove(focusClassName));

    let descendant = selection.focusNode;
    while (container.contains(descendant)) {
        if (
            descendant instanceof Element &&
            (descendant.classList.contains('atlas-block') || descendant.classList.contains('atlas-text'))
        ) {
            descendant.classList.add(focusClassName);
        }
        if (!descendant.parentNode) break;
        descendant = descendant.parentNode;
    }
}

// #endregion

// #region Types

export type TRichInputRef = Editor;

export type TRichInputDescendant = Descendant;

export type TRichInputValue = TRichInputDescendant[];

export type TRichInputChangeHandler = (value: TRichInputValue) => void;

export type TRichInputSelectHandler = (selection: BaseSelection) => void;

export type TRichInputRange = BaseRange;

export type TRichInputContextBuilder = (
    editor: Editor,
    close: () => void,
    change?: 'value' | 'selection',
    onMention?: (isCustomer?: boolean) => void,
) => [view: React.ReactNode, keyDownHandler?: null | React.KeyboardEventHandler] | null | undefined;

export type TRichInputBlockMenu = ({ node, editor }: { node: SlateBlock; editor: Editor }) => React.ReactNode;

export type TRichInputProps = Pick<
    React.ComponentProps<typeof Editable>,
    | 'className'
    | 'style'
    | 'disabled'
    | 'readOnly'
    | 'placeholder'
    | 'onFocus'
    | 'onBlur'
    | 'onKeyDown'
    | 'onMouseDown'
    | 'onDOMBeforeInput'
    | 'onPaste'
    | 'scrollSelectionIntoView'
> & {
    // Add class name to the textbox
    inputClassName?: string;

    // Default behavior of the `onChange` is changed to separate changes of value and selection
    onChange?: TRichInputChangeHandler;
    onSelect?: TRichInputSelectHandler;

    // Temporary option to pass configured editor (need to adopt older RE now)
    editor?: Editor;

    // Using `value` may cause performance issues.
    // Prefer `defaultValue` and `editor` properties to set and modify the content
    value?: TRichInputValue;
    defaultValue?: TRichInputValue;

    // This property can be used to customize what is rendered within <Slate> scope
    children?: (input?: React.ReactElement) => React.ReactNode;

    // Limits block and marks that can be used in the editor
    features?: TEditorFeature[] | null;

    // Display content next to the selection
    buildContext?: TRichInputContextBuilder;

    // Define context menu for block
    blockMenu?: TRichInputBlockMenu;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        const style: React.CSSProperties = {
            width: '400px',
            minHeight: '50px',
            maxHeight: '200px',
            border: '1px dashed #ccc',
        };

        return (
            <RichInput
                defaultValue={[{ type: 'paragraph', children: [{ text: 'Hello World!' }] }]}
                onChange={props.onChange}
                onSelect={props.onSelect}
                placeholder="Type in..."
                style={style}
            />
        );
    },
    handlers: {
        onChange: 'console',
        onSelect: 'console',
    },
};

export const InlineExample: TExample = {
    render(props) {
        const style: React.CSSProperties = useMemo(
            () => ({
                width: '400px',
                minHeight: '50px',
                maxHeight: '200px',
                border: '1px dashed #ccc',
            }),
            [],
        );

        const defaultValue = useMemo<TRichInputValue>(
            () => [
                {
                    type: 'paragraph',
                    children: [
                        {
                            text: 'Amet anim aliquip do labore anim et Lorem officia sint sint. Anim mollit do reprehenderit est dolore qui et aliquip et qui aliqua deserunt consectetur sunt. Aute commodo ut aliqua cupidatat minim nisi aliqua commodo officia ut qui commodo dolore Lorem officia. Ut dolor cupidatat incididunt exercitation aliqua consequat nostrud sit excepteur. Exercitation reprehenderit nostrud anim dolore non deserunt consectetur fugiat cupidatat laboris laborum. Eiusmod incididunt incididunt esse consectetur culpa dolore laborum eiusmod proident. Qui aliqua ea reprehenderit cillum ullamco minim eiusmod laborum culpa excepteur proident magna. Reprehenderit ipsum ipsum Lorem eu amet reprehenderit adipisicing pariatur.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    children: [
                        {
                            text: 'Proident ipsum veniam id pariatur elit sint cillum officia ipsum cupidatat do incididunt. Tempor adipisicing cupidatat dolore adipisicing elit excepteur exercitation sint ea proident. Nulla et adipisicing cupidatat fugiat enim tempor tempor Lorem. Ut voluptate elit sint minim quis dolor sit elit eu quis laboris eiusmod nostrud minim occaecat. Dolore exercitation consequat ullamco occaecat aute. Ut deserunt laboris et aliquip consequat magna. Incididunt eu labore eiusmod qui enim ipsum sint anim cillum ut quis. Lorem laborum exercitation consectetur culpa quis voluptate labore nisi nostrud ea id.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    children: [
                        {
                            text: 'Veniam duis mollit id est dolor proident ad pariatur id ipsum nostrud voluptate ad exercitation. Nulla qui esse do pariatur excepteur. Veniam voluptate irure culpa qui velit in laboris consequat. Eiusmod velit nostrud commodo reprehenderit veniam dolore ex irure laboris qui et pariatur pariatur. Eiusmod esse do eu ut tempor quis eiusmod. Dolor cillum adipisicing et minim magna aliquip qui pariatur reprehenderit elit commodo ullamco quis. Cillum culpa enim labore officia eu.',
                        },
                    ],
                },
            ],
            [],
        );

        const buildContext = useCallback<TRichInputContextBuilder>((editor) => {
            const { selection } = editor;
            if (!selection) return;

            const tooltipStyle: React.CSSProperties = {
                background: 'black',
                color: 'white',
                padding: '0 0.4rem',
                borderRadius: '0.5rem',
                fontSize: '1.1rem',
                maxWidth: '20.0rem',
            };

            if (Range.isCollapsed(selection)) {
                const precedingText = Editor.string(editor, {
                    ...selection,
                    focus: Editor.start(editor, selection.anchor.path.slice(0, -1)),
                });

                const precedingWordMatch = precedingText.match(/\w+$/);
                if (!precedingWordMatch) return;

                const [precedingWord] = precedingWordMatch;
                if (!precedingWord) return;

                return [
                    <div style={tooltipStyle}>
                        Preceding word: <strong>{precedingWord}</strong>
                    </div>,
                ];
            }

            const selectedString = Editor.string(editor, selection);

            return [
                <div style={tooltipStyle}>
                    Selected string: <strong>{selectedString}</strong>
                </div>,
            ];
        }, []);

        return (
            <RichInput
                defaultValue={defaultValue}
                onChange={props.onChange}
                onSelect={props.onSelect}
                placeholder="Type in..."
                style={style}
                buildContext={buildContext}
            />
        );
    },
    handlers: {
        onChange: 'console',
        onSelect: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest;
    describe('RichInput', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        test('matches snapshot', async () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
